/* eslint-disable max-lines */
import {
  addDays,
  addHours,
  addMinutes,
  differenceInDays,
  differenceInMinutes,
  format,
  getDay as getWeekDay,
  isAfter,
  isBefore,
  parse
} from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { t } from 'i18next';

import { deserializer } from '~services/baseSerializers';
import { CONFIG_COUNTRIES, CURRENT_COUNTRY_ID } from '~constants/environment';

export const DAY_ENDS = '2000-01-01T23:59:59.999Z';
export const DAY_STARTS = '2000-01-01T00:00:00.000Z';
export const TIMER_SECOND = 1_000;
export const TIMER_MINUTE = TIMER_SECOND * 60;
export const TIMER_HOUR = TIMER_MINUTE * 60;
export const TIMER_DAY = TIMER_HOUR * 24;
export const TIMER_ENDED = ' 00:00:00';
export const TEXT_DAY = 'día';
export const TEXT_DAYS = 'días';

export const DAYS_OF_THE_WEEK = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday'
};

const TIME_ZONE = process.env.REACT_APP_TIME_ZONE;

export const DAYS = Object.values(DAYS_OF_THE_WEEK).map(day => day);

const toValidDate = date => {
  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

export const dateToTime = date => {
  const parsedDate = toValidDate(date);
  return parsedDate ? formatInTimeZone(parsedDate, 'UTC', 'HH:mm') : '';
};

export const getDay = day =>
  ({
    [DAYS_OF_THE_WEEK.MONDAY]: t('StoreDetail:monday'),
    [DAYS_OF_THE_WEEK.TUESDAY]: t('StoreDetail:tuesday'),
    [DAYS_OF_THE_WEEK.WEDNESDAY]: t('StoreDetail:wednesday'),
    [DAYS_OF_THE_WEEK.THURSDAY]: t('StoreDetail:thursday'),
    [DAYS_OF_THE_WEEK.FRIDAY]: t('StoreDetail:friday'),
    [DAYS_OF_THE_WEEK.SATURDAY]: t('StoreDetail:saturday'),
    [DAYS_OF_THE_WEEK.SUNDAY]: t('StoreDetail:sunday')
  })[day];

export const getTodayString = () => format(new Date(), 'EEEE').toLowerCase();

const getRange = (opening, closing) =>
  opening === closing ? t('StoreDetail:allDay') : `${dateToTime(opening)} - ${dateToTime(closing)}`;

export const mergeSchedules = schedules => {
  const sortedSchedules = [...schedules].sort((a, b) => a.position - b.position);

  const allSchedules = sortedSchedules?.reduce((newSchedules, entry) => {
    const currentEntry = newSchedules.find(_entry => _entry?.startDay === getDay(entry.day));
    const range = getRange(entry.openingTime, entry.closingTime);
    let newEntry = null;

    if (currentEntry) {
      currentEntry.range = `${currentEntry.range}, ${range}`;
    } else {
      newEntry = {
        range,
        day: entry.day,
        startDay: getDay(entry.day)
      };
    }

    newSchedules.push(newEntry);

    return newSchedules;
  }, []);

  return allSchedules.filter(Boolean);
};

export const normalizeBusinessHours = (data, waitTime, rawRanges = false) =>
  data?.reduce((businessHours, { day, id, openingTime, closingTime, dispatchMethod }) => {
    const currentDayIndex = DAYS.indexOf(day);
    const newEntries = businessHours?.[dispatchMethod] || [];
    const nextDayFirstEntry = data.find(
      entry =>
        DAYS.indexOf(entry.day) === currentDayIndex + 1 &&
        entry.openingTime === DAY_STARTS &&
        entry.dispatchMethod === dispatchMethod
    );
    const newClosingTime =
      closingTime === DAY_ENDS && nextDayFirstEntry?.openingTime === DAY_STARTS
        ? nextDayFirstEntry.closingTime
        : closingTime;

    const isDayEnds = newClosingTime === DAY_ENDS ? DAY_STARTS : newClosingTime;

    const newEntry = {
      id,
      day,
      openingTime,
      closingTime: rawRanges ? closingTime : isDayEnds,
      position: currentDayIndex
    };

    if ((openingTime === DAY_STARTS && closingTime === DAY_ENDS) || openingTime !== DAY_STARTS || rawRanges) {
      newEntries.push(newEntry);
    }

    businessHours[dispatchMethod] = newEntries;
    return businessHours;
  }, {});

export const formatDate = date => {
  const parsedDate = toValidDate(date);
  return parsedDate ? format(parsedDate, 'dd/MM/yyyy') : '';
};

export const buildDateES = date => {
  const verifiedDate = new Date(date);
  return `${verifiedDate.getDate()} de ${verifiedDate.toLocaleDateString('es-ES', {
    month: 'long'
  })} de ${verifiedDate.getFullYear()}`;
};

export const formatDateTimestamp = date => formatInTimeZone(new Date(date), TIME_ZONE, 'dd/MM/yyyy, HH:mm');

export const handleTransformDate = date => {
  if (!date) {
    return undefined;
  }

  const originalDate = new Date(date);
  if (Number.isNaN(originalDate.getTime())) {
    return undefined;
  }

  const tz = CONFIG_COUNTRIES[CURRENT_COUNTRY_ID].timezone;
  const pad = num => `${num}`.padStart(2, '0');
  const wallClock = `${originalDate.getFullYear()}-${pad(originalDate.getMonth() + 1)}-${pad(
    originalDate.getDate()
  )}T12:00:00`;
  const zonedDate = fromZonedTime(wallClock, tz);

  if (Number.isNaN(zonedDate.getTime())) {
    return undefined;
  }

  return zonedDate.toISOString();
};

// Mirrors moment().utc(true): keeps the current local wall-clock values but reinterprets them as
// UTC, shifting the underlying instant by the timezone offset.
export const currentTimestamp = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * TIMER_MINUTE);
};

export const businessHoursDelivery = (data, waitTime, pickUp) => {
  const serializedHours = data && deserializer.serialize(normalizeBusinessHours(data, waitTime, true));
  const weekBusinessHours = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: []
  };
  serializedHours?.[pickUp ? 'inStore' : 'pjDelivery']?.map(item =>
    weekBusinessHours[item.day].push({
      id: item.id,
      openingTime: item.openingTime,
      closingTime: item.closingTime
    })
  );
  return weekBusinessHours;
};

export const dayFormat = stringDateFormat => {
  const date = new Date(stringDateFormat);
  return `${getDay(format(date, 'EEEE').toLowerCase())}.${format(date, 'dd/MM')}`;
};

const RANGE = 10;

export const timeRangeFormat = stringDateFormat => {
  const date = new Date(stringDateFormat);
  return `${format(date, 'p')} - ${format(addMinutes(date, RANGE), 'p')}`.toLowerCase();
};

const dateToShowFormat = (date, day, number) =>
  ({
    0: t('SelectTimeDispatch:today'),
    1: t('SelectTimeDispatch:tomorrow')
  })[number] || `${getDay(day)} ${format(date, 'dd/MM')}`;

const LAST_HOUR = 24;
const COUNT_DAYS_WEEK = 7;
const QUARTER_AFTER_HOUR = 15;
const QUARTER_TILL_HOUR = 45;

const normalizeTime = ({ date, isOpeningTime }) => {
  let minutes = date.getMinutes();
  let hours = date.getHours();
  if (minutes === QUARTER_AFTER_HOUR) {
    minutes = isOpeningTime ? RANGE : 0;
  }
  if (minutes === QUARTER_TILL_HOUR) {
    hours += isOpeningTime ? 1 : 0;
    minutes = isOpeningTime ? 0 : RANGE;
  }
  date.setHours(hours);
  date.setMinutes(minutes);
};

// startTime/endTime are instants displayed in UTC (moment .utc()), so they use UTC getters.
// limitPermited/waitTimeOffset are local-mode dates, so they use local getters.
const validEndTime = (endTime, limitPermited) => {
  const endTimeHours = endTime.getUTCHours() === 0 ? LAST_HOUR : endTime.getUTCHours();
  const isSameHour =
    endTimeHours === limitPermited.getHours() ? endTime.getUTCMinutes() <= limitPermited.getMinutes() : false;
  return endTimeHours >= limitPermited.getHours() ? isSameHour : true;
};

const validStartTime = (startTime, waitTimeOffset) => {
  const isSameHour =
    startTime.getUTCHours() === waitTimeOffset.getHours()
      ? startTime.getUTCMinutes() >= waitTimeOffset.getMinutes()
      : false;
  return startTime.getUTCHours() > waitTimeOffset.getHours() ? true : isSameHour;
};

const addMinutesInPlace = (date, minutes) => {
  date.setMinutes(date.getMinutes() + minutes);
  return date;
};

const getDeliveryRanges = ({
  businessHours,
  waitTimeOffset,
  hoursPermited,
  limitPermited,
  deliveryWaitTime
}) =>
  businessHours.reduce((ranges, { openingTime, closingTime }) => {
    const openingTimeNormalize = new Date(openingTime);
    normalizeTime({ date: openingTimeNormalize, isOpeningTime: true });

    const closingTimeNormalize = new Date(closingTime);
    normalizeTime({ date: closingTimeNormalize });

    const openingTimeHours = openingTimeNormalize.getUTCHours();

    const closingTimeHours = closingTime === DAY_ENDS ? LAST_HOUR : closingTimeNormalize.getUTCHours();
    const lastHourPermited = closingTimeHours > hoursPermited ? hoursPermited : closingTimeHours;

    if (lastHourPermited > openingTimeHours) {
      Array(Math.ceil(differenceInMinutes(closingTimeNormalize, openingTimeNormalize) / RANGE))
        .fill()
        // eslint-disable-next-line array-callback-return
        .map((_, index) => {
          const startTime = addMinutes(openingTimeNormalize, RANGE * index + deliveryWaitTime);

          const endTime = addMinutes(openingTimeNormalize, RANGE * (index + 1) + deliveryWaitTime);

          if (
            (waitTimeOffset ? validStartTime(startTime, waitTimeOffset) : true) &&
            (hoursPermited === LAST_HOUR || validEndTime(endTime, limitPermited)) &&
            (new Date(closingTime).getTime() - endTime.getTime() >= 0 ||
              (closingTime === DAY_ENDS && endTime.getUTCHours() === 0 && endTime.getUTCMinutes() === 0))
          ) {
            ranges.push({
              start: startTime,
              end: endTime,
              value: index,
              name: `${formatInTimeZone(startTime, 'UTC', 'HH:mm')} - ${formatInTimeZone(
                endTime,
                'UTC',
                'HH:mm'
              )}`.toLowerCase()
            });
          }
        });
    }
    return ranges;
  }, []);

export const getDaysRange = (hoursAmount, deliveryWaitTime, businessHours) => {
  const dateNow = new Date();
  const limitPermited = addHours(new Date(), hoursAmount);
  const normalizeDay =
    getWeekDay(limitPermited) < getWeekDay(dateNow)
      ? getWeekDay(limitPermited) + COUNT_DAYS_WEEK
      : getWeekDay(limitPermited);
  const daysDiff =
    normalizeDay - getWeekDay(dateNow) === 0 && differenceInDays(limitPermited, dateNow) > 0
      ? COUNT_DAYS_WEEK
      : 0;

  const daysRange = Array(Math.max(normalizeDay - getWeekDay(dateNow), daysDiff) + 1)
    .fill()
    .reduce((days, _, index) => {
      const date = addDays(new Date(), index);
      const day = format(date, 'EEEE').toLowerCase();
      const diffDays = getWeekDay(limitPermited) - getWeekDay(date);
      const diffHours = Math.floor((limitPermited.getTime() - date.getTime()) / TIMER_HOUR);

      const isSameDay = diffHours > LAST_HOUR ? LAST_HOUR : limitPermited.getHours();
      const hoursPermited = diffDays === 0 ? isSameDay : LAST_HOUR;
      days.push({
        date,
        id: `${date}`,
        text: dateToShowFormat(date, day, index),
        deliveryRanges: getDeliveryRanges({
          businessHours: businessHours[day],
          waitTimeOffset: index === 0 ? addMinutesInPlace(date, deliveryWaitTime) : null,
          hoursPermited,
          limitPermited,
          deliveryWaitTime
        })
      });
      return days;
    }, []);
  return daysRange.filter(item => !!item.deliveryRanges.length);
};

export const validateTimeRange = (startTime, endTime) => {
  const reference = new Date();
  const startTimeParse = parse(startTime, 'HH:mm', reference);
  const endTimeParse = parse(endTime, 'HH:mm', reference);
  const rangeTimeParse = formatInTimeZone(new Date(), TIME_ZONE, 'HH:mm');
  const currentParse = parse(rangeTimeParse, 'HH:mm', reference);
  return isAfter(currentParse, startTimeParse) && isBefore(currentParse, endTimeParse);
};

export const getMinutesDifference = (startTime, endTime) =>
  (new Date(startTime).getTime() - new Date(endTime).getTime()) / TIMER_MINUTE;

export const getDateValue = date => new Date(date).getTime();

export const getTimeToFinish = timespan => {
  const days = Math.floor(timespan / TIMER_DAY);
  const hours = Math.floor((timespan / TIMER_HOUR) % 24);
  const minutes = Math.floor((timespan / TIMER_MINUTE) % 60);
  const seconds = Math.floor((timespan / TIMER_SECOND) % 60);
  const daysToFinish = `${days} ${days === 1 ? TEXT_DAY : TEXT_DAYS}`;

  const daysUntil = days > 0 ? daysToFinish : '';
  const hoursUntil = hours.toString().padStart(2, '0');
  const minutesUntil = minutes.toString().padStart(2, '0');
  const secondsUntil = seconds.toString().padStart(2, '0');

  const hoursToFinish = `${hoursUntil}:${minutesUntil}:${secondsUntil}`;

  return ` ${daysUntil} ${hoursToFinish}`;
};

export function formatTimeToMinutesAndSeconds(time) {
  const minutes = `${Math.floor(time / 60)}`.padStart(2, '0');
  const seconds = `${time - minutes * 60}`.padStart(2, '0');
  return `${minutes}:${seconds}`;
}
